// Remaining V0/simple sections: eligibility, inclusion decision, biopsy,
// metastasis, past treatments, EoS, and drug administration (F1901). Ported
// from source lines 5526-5711 (biopsy/metastasis/past_tx/drug_admin) and
// 6621-6785, 7019-7042 (eligibility/inclusion/EoS). Biopsy's anatomic-site
// picker and metastasis's site picker are simplified to a single multi-select
// rather than the source's grouped checkbox grid - a disclosed simplification
// (the same sites list, same stored shape, just a plainer widget).
import { escapeHtml } from "./dom.js";
import { renderField } from "./section-fields.js";
import { INCLUSION_CRITERIA, NON_INCLUSION_CRITERIA, SCREEN_FAILURE_REASONS, EOS_REASONS, computeDrugAdminQc } from "@woof/shared";

export function renderEligibilitySection(v: Record<string, any>, writable: boolean): string {
  const allInc = !!v.inc_all_yes;
  const allExc = !!v.exc_all_no;
  const incRows = INCLUSION_CRITERIA.map((c, i) => {
    const k = `inc_${i + 1}`;
    const val = allInc ? "Oui" : v[k] || "";
    const opts = ["", "Oui", "Non", "NA"].map((o) => `<option value="${o}" ${val === o ? "selected" : ""}>${o || "—"}</option>`).join("");
    return `<tr><td><strong>I${i + 1}</strong></td><td>${escapeHtml(c)}</td><td><select data-fkey="${k}" ${writable && !allInc ? "" : "disabled"}>${opts}</select></td></tr>`;
  }).join("");
  const excRows = NON_INCLUSION_CRITERIA.map((c, i) => {
    const k = `exc_${i + 1}`;
    const val = allExc ? "Non" : v[k] || "";
    const opts = ["", "Oui", "Non", "NA"].map((o) => `<option value="${o}" ${val === o ? "selected" : ""}>${o || "—"}</option>`).join("");
    return `<tr><td><strong>E${i + 1}</strong></td><td>${escapeHtml(c)}</td><td><select data-fkey="${k}" ${writable && !allExc ? "" : "disabled"}>${opts}</select></td></tr>`;
  }).join("");

  return `
    <h4 style="margin:0 0 8px 0">Critères d'inclusion (${INCLUSION_CRITERIA.length})</h4>
    <label class="cbx" style="margin-bottom:8px;background:#ecfdf5;padding:8px 12px;border-radius:6px;border:1px solid #6ee7b7">
      <input type="checkbox" data-fkey="inc_all_yes" ${allInc ? "checked" : ""} ${writable ? "" : "disabled"}>
      <span><strong>Tous les critères d'inclusion sont remplis</strong></span>
    </label>
    <table class="data-table"><thead><tr><th>#</th><th>Critère</th><th>Rempli</th></tr></thead><tbody>${incRows}</tbody></table>
    <h4 style="margin:18px 0 8px 0">Critères de non-inclusion (${NON_INCLUSION_CRITERIA.length})</h4>
    <label class="cbx" style="margin-bottom:8px;background:#ecfdf5;padding:8px 12px;border-radius:6px;border:1px solid #6ee7b7">
      <input type="checkbox" data-fkey="exc_all_no" ${allExc ? "checked" : ""} ${writable ? "" : "disabled"}>
      <span><strong>Aucun critère de non-inclusion présent</strong></span>
    </label>
    <table class="data-table"><thead><tr><th>#</th><th>Critère</th><th>Présent</th></tr></thead><tbody>${excRows}</tbody></table>`;
}

export function renderInclusionSection(v: Record<string, any>, elig: Record<string, any>, writable: boolean): string {
  const failedInclusion: number[] = [];
  for (let i = 1; i <= INCLUSION_CRITERIA.length; i++) if (elig[`inc_${i}`] === "Non") failedInclusion.push(i);
  const presentExclusion: number[] = [];
  for (let i = 1; i <= NON_INCLUSION_CRITERIA.length; i++) if (elig[`exc_${i}`] === "Oui") presentExclusion.push(i);
  const eligibilityForcesFailure = failedInclusion.length > 0 || presentExclusion.length > 0;
  const showFailure = v.decision === "Screen failure";
  const incoherence = v.decision === "Inclus" && eligibilityForcesFailure;

  const optDec = ["", "Inclus", "Screen failure", "En attente"].map((o) => `<option value="${o}" ${v.decision === o ? "selected" : ""}>${o || "— Sélectionner —"}</option>`).join("");
  const optReason = ["", ...SCREEN_FAILURE_REASONS].map((o) => `<option value="${escapeHtml(o)}" ${v.failure_reason_type === o ? "selected" : ""}>${escapeHtml(o || "— Sélectionner —")}</option>`).join("");

  return `
    <div class="form-grid">
      <div class="form-group"><label>Décision <span class="required-mark">*</span></label><select data-fkey="decision" ${writable ? "" : "disabled"}>${optDec}</select></div>
      ${renderField("Date décision", "date", "date", v.date, writable)}
      ${renderField("Investigateur", "investigator", "text", v.investigator, writable)}
      ${renderField("Date signature ICF", "icf_date", "date", v.icf_date, writable)}
    </div>
    ${incoherence ? `<div class="alert-sae" style="margin-top:10px"><strong>⚠️ Incohérence :</strong> décision "Inclus" mais des critères d'éligibilité l'excluent. Une query devrait être ouverte.</div>` : ""}
    <div style="display:${showFailure ? "block" : "none"};margin-top:14px;padding:14px;background:#fffbeb;border:1px solid #fde68a;border-radius:8px">
      <h4 style="margin:0 0 10px 0;color:#92400e">Motif de Screen Failure</h4>
      ${eligibilityForcesFailure ? `<div class="alert-pv" style="background:#eff6ff;border-color:#3b82f6;color:#1e3a8a">${failedInclusion.length ? `Critères d'inclusion non remplis : I${failedInclusion.join(", I")}<br>` : ""}${presentExclusion.length ? `Critères de non-inclusion présents : E${presentExclusion.join(", E")}` : ""}</div>` : ""}
      <div class="form-grid">
        <div class="form-group"><label>Type de motif <span class="required-mark">*</span></label><select data-fkey="failure_reason_type" ${writable ? "" : "disabled"}>${optReason}</select></div>
        ${renderField("N° critère(s) concerné(s)", "failure_criterion_num", "text", v.failure_criterion_num, writable)}
      </div>
      ${renderField("Commentaire / précision", "failure_reason_detail", "textarea", v.failure_reason_detail, writable)}
    </div>`;
}

export function renderBiopsySection(v: Record<string, any>, writable: boolean): string {
  return `<div class="form-grid">
      ${renderField("Date biopsie", "date", "date", v.date, writable)}
      ${renderField("Diagnostic histologique", "histology_result", "textarea", v.histology_result, writable, { rows: 3 })}
      ${renderField("Laboratoire", "lab", "text", v.lab, writable)}
    </div>
    ${renderField("Sites biopsiés (texte libre)", "sites_text", "textarea", v.sites_text, writable)}
    ${renderField("Notes", "notes", "textarea", v.notes, writable)}`;
}

export function renderMetastasisSection(v: Record<string, any>, writable: boolean): string {
  const status = v.status || "";
  const opts = ["", "Absent", "Présent", "ND"].map((o) => `<option value="${o}" ${status === o ? "selected" : ""}>${o || "— Sélectionner —"}</option>`).join("");
  return `
    <div class="form-group"><label>Statut métastatique</label><select data-fkey="status" ${writable ? "" : "disabled"}>${opts}</select></div>
    <div style="display:${status === "Présent" ? "block" : "none"}">
      ${renderField("Sites concernés (texte libre)", "sites_text", "textarea", v.sites_text, writable)}
    </div>`;
}

export function renderPastTxSection(v: Record<string, any>, writable: boolean): string {
  const checkbox = (key: string, label: string) => `<label class="cbx"><input type="checkbox" data-fkey="${key}" ${v[key] ? "checked" : ""} ${writable ? "" : "disabled"}><span>${label}</span></label>`;
  return `
    <div class="cbx-stack" style="margin-bottom:14px">
      ${checkbox("surgery", "Chirurgie antérieure")}
      ${checkbox("radiotherapy", "Radiothérapie antérieure")}
      ${checkbox("chemo", "Chimiothérapie antérieure")}
      ${checkbox("planned_surgery", "Chirurgie prévue après l'étude")}
    </div>
    <div class="form-grid">
      ${renderField("Détails chirurgie", "surgery_detail", "textarea", v.surgery_detail, writable)}
      ${renderField("Détails radiothérapie", "radiotherapy_detail", "textarea", v.radiotherapy_detail, writable)}
      ${renderField("Détails chimiothérapie", "chemo_detail", "textarea", v.chemo_detail, writable)}
    </div>`;
}

export function renderEosSection(v: Record<string, any>, writable: boolean): string {
  const opts = ["", ...EOS_REASONS.map((r) => r.key)].map((k) => {
    const label = EOS_REASONS.find((r) => r.key === k)?.label ?? "— Sélectionner —";
    return `<option value="${k}" ${v.dsterm === k ? "selected" : ""}>${escapeHtml(label)}</option>`;
  }).join("");
  return `
    <p class="field-hint">Le formulaire EoS clôture la participation du patient à l'étude.</p>
    <div class="form-grid">
      ${renderField("Date fin étude", "dsstdat", "date", v.dsstdat, writable, { required: true })}
      <div class="form-group"><label>Motif <span class="required-mark">*</span></label><select data-fkey="dsterm" ${writable ? "" : "disabled"}>${opts}</select></div>
    </div>
    ${renderField("Motif détaillé / commentaire", "reason_detail", "textarea", v.reason_detail, writable)}
    <div class="form-grid">
      ${renderField("Investigateur (signature)", "signed_by", "text", v.signed_by, writable, { required: true })}
      ${renderField("Date signature", "signed_date", "date", v.signed_date, writable, { required: true })}
    </div>`;
}

export function renderDrugAdminSection(v: Record<string, any>, derived: any, patientId: string, writable: boolean): string {
  const qc = derived?.drugAdminQc;
  const utNumber = patientId.split("-").slice(0, 2).join("-");
  let qcBlock = "";
  if (qc) {
    const colorMap: Record<string, { bg: string; border: string; text: string; icon: string }> = {
      ok: { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46", icon: "✓" },
      low: { bg: "#fffbeb", border: "#fde68a", text: "#92400e", icon: "⚠" },
      high: { bg: "#fef2f2", border: "#fca5a5", text: "#7f1d1d", icon: "⚠" },
      unknown: { bg: "#f3f4f6", border: "#d1d5db", text: "#374151", icon: "ⓘ" },
    };
    const c = colorMap[qc.status as string] ?? colorMap.unknown!;
    qcBlock = qc.tumorVolume
      ? `<div style="margin-top:8px;padding:10px 14px;background:${c.bg};border:1px solid ${c.border};border-radius:8px;color:${c.text};font-size:13px">
          <strong>${c.icon} Contrôle de cohérence</strong> (volume tumoral : ${qc.tumorVolume.toFixed(2)} cm³)<br>
          Fourchette protocolaire : <strong>${qc.minML.toFixed(2)} – ${qc.maxML.toFixed(2)} mL</strong> (10–15% du volume tumoral).
        </div>`
      : `<div style="margin-top:8px;padding:10px 14px;background:#f3f4f6;border:1px solid #d1d5db;border-radius:8px;font-size:12px">ⓘ Volume tumoral non disponible : saisir d'abord les mesures caliper ou CT scan.</div>`;
  }
  return `<div class="form-grid">
      ${renderField("Date administration", "exstdat", "date", v.exstdat, writable)}
      ${renderField("Heure administration", "time", "time", v.time, writable)}
      ${renderField("Volume injecté (mL)", "exdose", "number", v.exdose, writable)}
      <div class="form-group"><label>N° unité de traitement F1901</label><input type="text" value="${escapeHtml(utNumber)}" disabled style="background:#f3f4f6;font-weight:600;color:#1e3a8a"></div>
      ${renderField("Date péremption", "expiry", "date", v.expiry, writable)}
      ${renderField("Site injection", "exloc", "text", v.exloc, writable)}
      ${renderField("Opérateur", "operator", "text", v.operator, writable)}
    </div>
    ${qcBlock}
    ${renderField("Notes", "notes", "textarea", v.notes, writable)}`;
}
